import React, { useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons'
import type { EmployeeProfile } from '../../services/profileService'

function employeeDirectoryLabel(employee: EmployeeProfile) {
  return (
    employee.name ||
    employee.preferredName ||
    `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
    'Unknown Employee'
  )
}

function employeeSortKey(employee: EmployeeProfile) {
  const last = String(employee.lastName || '').trim().toLowerCase()
  const first = String(employee.firstName || '').trim().toLowerCase()
  const label = employeeDirectoryLabel(employee).toLowerCase()
  return `${last}\u0000${first}\u0000${label}\u0000${employee.employeeId}`
}

function employeeMatchesQuery(employee: EmployeeProfile, needle: string) {
  const haystack = [
    employee.employeeId,
    employee.name,
    employee.firstName,
    employee.lastName,
    employee.preferredName,
    employee.position,
    employee.department,
    employee.supervisor,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}

type HrEmployeeDirectoryProps = {
  employees: EmployeeProfile[]
  loading?: boolean
  error?: string | null
  searchTerm: string
  onSearchTermChange: (value: string) => void
  selectedEmployeeId: string
  onSelect: (employeeId: string) => void
}

const HrEmployeeDirectory: React.FC<HrEmployeeDirectoryProps> = ({
  employees,
  loading = false,
  error = null,
  searchTerm,
  onSearchTermChange,
  selectedEmployeeId,
  onSelect,
}) => {
  const needle = searchTerm.trim().toLowerCase()
  const filtered = useMemo(() => {
    const ranked = [...employees].sort((a, b) => employeeSortKey(a).localeCompare(employeeSortKey(b)))
    if (!needle) return ranked
    return ranked.filter((employee) => employeeMatchesQuery(employee, needle))
  }, [employees, needle])

  const countLabel = needle
    ? `${filtered.length} of ${employees.length}`
    : `${employees.length} employee${employees.length === 1 ? '' : 's'}`

  return (
    <div className="card bg-base-100 shadow-xl flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="card-body flex min-h-0 flex-1 flex-col gap-3 p-4">
        <div className="flex items-baseline justify-between gap-3 shrink-0">
          <h2 className="card-title text-base">Employees</h2>
          <span className="text-xs text-base-content/60 whitespace-nowrap">{loading && !employees.length ? 'Loading…' : countLabel}</span>
        </div>

        <label className="input input-bordered flex items-center gap-2 shrink-0 min-h-11">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="text-base-content/60 shrink-0" />
          <input
            type="text"
            className="grow min-w-0"
            placeholder="Search name, ID, or position"
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            aria-label="Search employees"
          />
          {searchTerm ? (
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-circle shrink-0"
              aria-label="Clear search"
              onClick={() => onSearchTermChange('')}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          ) : null}
        </label>

        {error && (
          <div className="alert alert-error py-2 shrink-0">
            <span>{error}</span>
          </div>
        )}

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-lg border border-base-300"
          role="listbox"
          aria-label="Employee list"
        >
          {loading && employees.length === 0 ? (
            <div className="flex items-center gap-2 p-4 text-sm text-base-content/70">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              Loading employees…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-sm text-base-content/70">
              {needle ? `No matches for “${searchTerm.trim()}”.` : 'No employees found.'}
            </div>
          ) : (
            filtered.map((employee) => {
              const selected = selectedEmployeeId === employee.employeeId
              const title = employeeDirectoryLabel(employee)
              const meta = [employee.employeeId, employee.position].filter(Boolean).join(' · ')
              return (
                <button
                  key={employee.employeeId}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`w-full text-left px-3 py-2.5 border-b border-base-200 last:border-b-0 border-l-4 ${
                    selected
                      ? 'bg-primary/10 border-l-primary'
                      : 'border-l-transparent hover:bg-base-200'
                  }`}
                  onClick={() => onSelect(employee.employeeId)}
                >
                  <div className="font-medium truncate">{title}</div>
                  <div className="text-xs text-base-content/60 truncate">{meta || 'ID not set'}</div>
                  {employee.department ? (
                    <div className="text-[11px] text-base-content/50 truncate">{employee.department}</div>
                  ) : null}
                </button>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

export default HrEmployeeDirectory
